import React, { useEffect, useMemo, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';
import { saveDownload } from '~libs/genericAction';

import { ClickableIcon, Spinner, TermTooltip, Toolbar } from '~components';
import Icon, { faInfoCircle, faRefresh } from '~components/icon';
import { Row, SortState } from '~components/table-view';

import { DashboardCaseManagerFilter } from '~support-at-home/models/dashboard';

import {
  DashboardExportTable,
  exportDashboardTable,
  useGetDashboardBudgetRowsQuery,
  useGetDashboardCareManagementQuery,
  useGetDashboardCmWorkersQuery,
  useGetDashboardSummaryQuery,
} from '~support-at-home/actions/dashboardBudgets';

import { CaseManagerSelect } from './CaseManagerSelect';
import { CompanyBudgetOverview } from './CompanyBudgetOverview';
import { CompanyCareManagementOverview } from './CompanyCareManagementOverview';
import { DashboardBanner } from './DashboardBanner';
import { BreakdownKey, buildBreakdowns, buildDashboardBanner, ListSide } from './dashboardFigures';
import { DashboardFullTable } from './DashboardFullTable';
import { DashboardList, ListView } from './DashboardList';
import { DashboardSummary } from './DashboardSummary';
import { DASHBOARD_TEXT } from './dashboardText';
import { MUTED_GREY, oldestComputedAt, whileRefreshing, widgetStatus } from './dashboardWidgets';
import { NoCmHoursCheckbox } from './NoCmHoursCheckbox';
import { QuarterYearSelector } from './QuarterYearSelector';
import { useDashboardRefresh } from './useDashboardRefresh';
import {
  buildCareManagementOverview,
  buildCompanyOverview,
  fullTableColumns,
  getCurrentPeriod,
  LIST_TEXT,
  listColumns,
  mapWorkersToRows,
  workerColumns,
} from './utils';

const copy = DASHBOARD_TEXT;
const WORKERS_KEY = 'workers';
const SIDES: ListSide[] = ['over', 'under'];

/** The lists in the order the page shows them (Utilisation and Care management, then Allocation and Workers). */
const LIST_KEYS: BreakdownKey[] = ['utilisation', 'care-management', 'allocation'];

const searchOfBudget = (row: Row): string => `${row.client_name} ${row.reference}`;
const searchOfWorker = (row: Row): string => `${row.worker_name}`;

const RANKED_BY_AMOUNT: SortState = [{ id: 'amount', direction: 'desc' }];
const RANKED_BY_HOURS_DESC: SortState = [{ id: 'cm_hours', direction: 'desc' }];
const RANKED_BY_HOURS_ASC: SortState = [{ id: 'cm_hours', direction: 'asc' }];

type WorkerOrder = 'most' | 'least';
const WORKER_ORDERS: WorkerOrder[] = ['most', 'least'];

/** Least Hours First: fewest hours, then name A to Z, then id (the api sends Most Hours First). */
const leastHoursFirst = (rows: Row[]): Row[] =>
  [...rows].sort(
    (a, b) =>
      Number(a.cm_hours) - Number(b.cm_hours) ||
      String(a.worker_name).localeCompare(String(b.worker_name), 'en-AU', { sensitivity: 'base' }) ||
      Number(a.id) - Number(b.id),
  );

interface FullTableView {
  title: string;
  columns: ListView['columns'];
  rows: Row[];
  searchLabel: string;
  compactSearch?: boolean;
  searchOf: (row: Row) => string;
  initialSortState: SortState;
  /** The api's name for this table, for Export All to XLSX. */
  exportTable: DashboardExportTable;
  tabs?: React.ComponentProps<typeof DashboardFullTable>['tabs'];
  filters?: React.ReactNode;
}

export const BudgetDashboard: React.FC = () => {
  const [{ quarter: initialQuarter, year: initialYear }] = useState(getCurrentPeriod);
  const [quarter, setQuarter] = useState(initialQuarter);
  const [year, setYear] = useState(initialYear);
  const [caseManager, setCaseManager] = useState<DashboardCaseManagerFilter>('all');
  // The side each list was switched to; a list not in here shows Over when it has any, otherwise Under
  const [sides, setSides] = useState<Partial<Record<BreakdownKey, ListSide>>>({});
  // Which full table is open: `${list}-${side}`, or 'workers'
  const [fullTable, setFullTable] = useState<string | null>(null);
  // Care management by worker: which order, and whether case managers with no hours are listed (no request for either)
  const [workerOrder, setWorkerOrder] = useState<WorkerOrder>('most');
  const [showNoCmHours, setShowNoCmHours] = useState(false);

  // One request per widget: each card shows its own spinner, error and Retry
  const args = useMemo(() => ({ quarter, year, caseManager }), [quarter, year, caseManager]);
  const summaryQuery = useGetDashboardSummaryQuery(args);
  const careManagementQuery = useGetDashboardCareManagementQuery(args);
  const budgetsQuery = useGetDashboardBudgetRowsQuery(args);
  const workersQuery = useGetDashboardCmWorkersQuery(args);

  // Refresh reloads all four widgets together, so every card shows its loading state until the last one is back
  const { refresh, isRefreshing, error: refreshError } = useDashboardRefresh(args);
  const summary = whileRefreshing(summaryQuery, isRefreshing);
  const careManagement = whileRefreshing(careManagementQuery, isRefreshing);
  const budgets = whileRefreshing(budgetsQuery, isRefreshing);
  const workers = whileRefreshing(workersQuery, isRefreshing);

  const summaryStatus = widgetStatus(summary);
  const budgetsStatus = widgetStatus(budgets);
  const companyStatus = widgetStatus(summary, budgets);
  const careManagementStatus = widgetStatus(careManagement, budgets);
  const workersStatus = widgetStatus(workers);

  // A quarter, year or case manager change would start a full load per step on the api, so they wait for the last
  const anyWidgetFetching = [summary, careManagement, budgets, workers].some((query) => query.isFetching);

  // currentData, not data: RTK Query keeps `data` from the last success for any args, so a card would mix the
  // quarter just left into the one loading (its over-limit outline, or summary and budgets of different quarters)
  const summaryData = summaryQuery.currentData;
  const careManagementData = careManagementQuery.currentData;
  const budgetRows = budgetsQuery.currentData?.budgets;
  const lastCalculated = oldestComputedAt(
    summaryData,
    careManagementData,
    budgetsQuery.currentData,
    workersQuery.currentData,
  );

  // A new quarter or case manager resets the toggles to their defaults
  useEffect(() => {
    setSides({});
  }, [budgetsQuery.currentData]);

  const breakdowns = useMemo(() => (budgetRows ? buildBreakdowns(budgetRows) : null), [budgetRows]);
  const banner = useMemo(() => (breakdowns ? buildDashboardBanner(breakdowns) : null), [breakdowns]);
  const companyView = useMemo(
    () =>
      summaryData && budgetRows
        ? buildCompanyOverview({
            summary: summaryData.summary,
            overview: summaryData.company_budget_overview,
            budgets: budgetRows,
          })
        : null,
    [summaryData, budgetRows],
  );
  const careManagementView = useMemo(
    () =>
      careManagementData && budgetRows
        ? buildCareManagementOverview({
            case_management_overview: careManagementData.case_management_overview,
            budgets: budgetRows,
          })
        : null,
    [careManagementData, budgetRows],
  );
  // Most Hours First is the api's order; the api always sends the case managers with no hours. The section lists only
  // workers with hours; the full table adds the others when Show Workers with No CM Hours is ticked
  const workerRows = useMemo(() => {
    const rows = workersQuery.currentData ? mapWorkersToRows(workersQuery.currentData.cm_workers) : [];
    const withHours = rows.filter((row) => row.has_cm_hours);
    const inTable = showNoCmHours ? rows : withHours;
    return {
      section: { most: withHours, least: leastHoursFirst(withHours) },
      table: { most: inTable, least: leastHoursFirst(inTable) },
    };
  }, [workersQuery.currentData, showNoCmHours]);

  const sideOf = (key: BreakdownKey): ListSide =>
    sides[key] ?? ((breakdowns?.[key].over.rows.length ?? 0) > 0 ? 'over' : 'under');

  const listViews = (key: BreakdownKey): ListView[] =>
    SIDES.map((side) => ({
      key: side,
      label: LIST_TEXT[key][side].label,
      total: breakdowns?.[key][side].total ?? 0,
      columns: listColumns(key, side),
      rows: breakdowns?.[key][side].rows ?? [],
      emptyText: LIST_TEXT[key][side].empty,
    }));

  const fullTableView = (id: string): FullTableView | null => {
    if (id === WORKERS_KEY) {
      return {
        title: copy.workers.fullTitle,
        columns: workerColumns,
        rows: workerRows.table[workerOrder],
        searchLabel: copy.workers.search,
        compactSearch: true,
        searchOf: searchOfWorker,
        initialSortState: workerOrder === 'most' ? RANKED_BY_HOURS_DESC : RANKED_BY_HOURS_ASC,
        exportTable: 'cm-workers',
        tabs: {
          items: WORKER_ORDERS.map((order) => ({ key: order, label: copy.workers.tabs[order] })),
          activeKey: workerOrder,
          onChange: (key) => setWorkerOrder(key as WorkerOrder),
        },
        filters: (
          <NoCmHoursCheckbox
            id="dashboard-full-table-no-cm-hours"
            checked={showNoCmHours}
            onChange={setShowNoCmHours}
          />
        ),
      };
    }
    const key = LIST_KEYS.find((candidate) => id.startsWith(`${candidate}-`));
    const side = SIDES.find((candidate) => id === `${key}-${candidate}`);
    if (!key || !side || !breakdowns) return null;
    return {
      title: LIST_TEXT[key][side].fullTitle,
      columns: fullTableColumns(key, side),
      rows: breakdowns[key][side].rows,
      searchLabel: copy.list.search,
      searchOf: searchOfBudget,
      initialSortState: RANKED_BY_AMOUNT,
      exportTable: `${key}-${side}`,
    };
  };

  const openFullTable = fullTable ? fullTableView(fullTable) : null;

  // Every row of the open table, whatever its search, sort, page or checkbox: the api builds the file
  const exportOpenTable = async (table: DashboardExportTable) =>
    saveDownload(await exportDashboardTable({ table, quarter, year, caseManager }), 'export.xlsx');

  const workerViews: ListView[] = WORKER_ORDERS.map((order) => ({
    key: order,
    label: copy.workers.tabs[order],
    columns: workerColumns,
    rows: workerRows.section[order],
    emptyText: copy.workers.empty,
  }));

  const renderList = (key: BreakdownKey) => (
    <div className="dashboard-section" key={key}>
      <DashboardList
        title={LIST_TEXT[key].title}
        description={LIST_TEXT[key].description}
        info={LIST_TEXT[key].info}
        views={listViews(key)}
        activeKey={sideOf(key)}
        onViewChange={(side) => setSides((current) => ({ ...current, [key]: side as ListSide }))}
        onShowAll={(side) => setFullTable(`${key}-${side}`)}
        dataCy={`dashboard-${key}-list`}
        status={budgetsStatus}
      />
    </div>
  );

  const renderContent = () => {
    if (summaryStatus.phase === 'ready' && summaryData?.summary.total_included_budgets === 0) {
      return (
        <div className="empty" role="status">
          <Icon icon={faInfoCircle} />
          {copy.noBudgets}
        </div>
      );
    }

    return (
      <>
        {budgetsStatus.phase === 'ready' && banner && <DashboardBanner banner={banner} />}

        <DashboardSummary
          summary={summaryData?.summary}
          overview={summaryData?.company_budget_overview}
          status={summaryStatus}
        />

        {/* The two overviews side by side, as on the Budget Tracker; the lists in the grid under them */}
        <div className="figure-overview-row">
          <CompanyBudgetOverview view={companyView} status={companyStatus} />
          <CompanyCareManagementOverview view={careManagementView} status={careManagementStatus} />
        </div>

        <div className="grid">
          {LIST_KEYS.map(renderList)}

          <div className="dashboard-section">
            <DashboardList
              title={copy.workers.title}
              description={copy.workers.description}
              info={copy.workers.info}
              views={workerViews}
              activeKey={workerOrder}
              onViewChange={(order) => setWorkerOrder(order as WorkerOrder)}
              onShowAll={() => setFullTable(WORKERS_KEY)}
              dataCy="dashboard-workers-list"
              status={workersStatus}
            />
          </div>
        </div>

        {openFullTable && (
          <DashboardFullTable
            key={fullTable}
            isOpen
            title={openFullTable.title}
            columns={openFullTable.columns}
            rows={openFullTable.rows}
            searchLabel={openFullTable.searchLabel}
            compactSearch={openFullTable.compactSearch}
            searchOf={openFullTable.searchOf}
            initialSortState={openFullTable.initialSortState}
            tabs={openFullTable.tabs}
            filters={openFullTable.filters}
            onExport={() => exportOpenTable(openFullTable.exportTable)}
            onClose={() => setFullTable(null)}
          />
        )}
      </>
    );
  };

  return (
    <div className="budget-dashboard" data-cy="budget-dashboard">
      <Toolbar
        id="budget-dashboard"
        leftCluster={
          <div className="dashboard-title">
            <h5 className="mb-0 fw-bold">{copy.header.title}</h5>
            {summaryStatus.phase === 'loading' && (
              <small className="dashboard-scope dashboard-scope-loading" data-cy="dashboard-included">
                <TermTooltip id="dashboard-included" label={copy.header.included} content={copy.header.includedHint} />
                {': '}
                <Spinner loading type="beat" size={6} color={MUTED_GREY} dataCy="dashboard-included-budgets-loading" />
                {` ${copy.header.budgetsLabel} · `}
                <Spinner loading type="beat" size={6} color={MUTED_GREY} dataCy="dashboard-included-clients-loading" />
                {` ${copy.header.clientsLabel}`}
              </small>
            )}
            {summaryStatus.phase === 'ready' && summaryData && (
              <small className="dashboard-scope" data-cy="dashboard-included">
                <TermTooltip id="dashboard-included" label={copy.header.included} content={copy.header.includedHint} />
                {copy.header.counts(
                  summaryData.summary.total_included_budgets,
                  summaryData.summary.total_unique_clients,
                )}
              </small>
            )}
          </div>
        }
        rightCluster={
          <div className="dashboard-controls">
            <div className="dashboard-filters">
              <CaseManagerSelect value={caseManager} onChange={setCaseManager} disabled={anyWidgetFetching} />
              <QuarterYearSelector
                quarter={quarter}
                year={year}
                onQuarterChange={setQuarter}
                onYearChange={setYear}
                disabled={anyWidgetFetching}
              />
            </div>
            {/* Under the filters: when the figures were worked out, and the icon that works them out again */}
            <div className="dashboard-recalculate">
              {lastCalculated && (
                <small className="dashboard-scope" data-cy="dashboard-last-calculated">
                  <TermTooltip
                    id="dashboard-last-calculated"
                    label={copy.refresh.lastCalculated}
                    content={copy.refresh.lastCalculatedHint}
                  />
                  {copy.refresh.time(dayjs(lastCalculated).format(dateTimeDisplayFormat))}
                </small>
              )}
              {/* The same refresh icon as the Budget Tracker; every card shows its spinner while it runs */}
              <ClickableIcon
                icon={faRefresh}
                id="dashboard-refresh-button"
                onClick={refresh}
                disabled={anyWidgetFetching}
                toolTip={copy.refresh.button}
                ariaLabel={copy.refresh.button}
                dataCy="dashboard-refresh"
              />
              {refreshError !== null && (
                <small className="text-danger" role="alert" data-cy="dashboard-refresh-failed">
                  {copy.refresh.failed(getErrorMessage(refreshError))}
                </small>
              )}
            </div>
          </div>
        }
      />
      {renderContent()}
    </div>
  );
};
